import { inject, Injectable } from '@angular/core';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import DataSource from 'devextreme/data/data_source';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LocalStorageFacade } from '@application/localstorage';
import { EPersonWithIncludes, IPerson, IPersonPhone } from '../entities/person.interface';
import { IPersonDataAsync } from '@person/user/self/data-access';
import { ISupplier } from '@goods/data-access';
import { IAuthAccessToken } from '@auth/data-access';
import { IPersonTax } from '../entities/tax/tax.interface';
import { IPersonComment } from '../entities/comment/comment.interface';
import { IPersonCertificate } from '../entities/certificate/certificate.interface';
import { IPersonAddress } from '../entities/address/address.interface';
import { IPersonAccess } from '../entities/access/access.interface';
import { IPersonPreferences } from '../entities/preferences/preferences.interface';
import { IPersonResponsible } from '../entities/responsible/responsible.interface';
import { IPersonIntegration } from '../entities/integration/integration.interface';
import { IPersonGroup } from '../entities/group/group.interface';
import { APP_CONFIG } from '@application/config';


@Injectable({providedIn: 'root'})
export class PersonService {
  private dxOdataService = inject(DxOdataStoreService);
  private http = inject(HttpClient);
  private localStorage = inject(LocalStorageFacade);
  private apiUrl = inject(APP_CONFIG).apiUrl;

  public getAllOfPersonsFromOdata(): DataSource {
    return this.dxOdataService.fromOdata({
      url: this.apiUrl.personService,
      metadata: 'OData/Persons',
      key: 'PersonId',
    });
  }

  public updatePerson(payload: Partial<IPerson>): Observable<IPerson> {
    return this.http.patch<IPerson>(
        this.apiUrl.personService + '/Person',
      payload,
      { withCredentials: true }
    );
  }

  public getPerson(personId: string): Observable<IPerson> {
    return this.http.get<IPerson>(
      this.apiUrl.personService + '/Person?id=' + personId,
      { withCredentials: true }
    );
  }

  public getPersonWithUserData(personId: string): Observable<IPerson> {
    return this.http.get<IPerson>(
        this.apiUrl.personService +
        '/Person/GetPersonWithUserConsolidated?personId=' + personId,
      { withCredentials: true }
    );
  }

  public deletePerson(personId: string): Observable<IPerson> {
    return this.http.delete<IPerson>(
      this.apiUrl.userService + '/Person?id= ' + personId,
      { withCredentials: true }
    );
  }

  public createPerson(person: Partial<IPerson>): Observable<IPerson> {
    return this.http.post<IPerson>(
      this.apiUrl.personService + '/Person',
      person,
      { withCredentials: true }
    );
  }

  public getPersonAddresses(personId: string): Observable<IPersonAddress[]> {
    return this.http.get<IPersonAddress[]>(
        this.apiUrl.personService +
        '/Address/GetByPerson?personId=' + personId,
      { withCredentials: true }
    );
  }

  public getPersonAddress(phoneId: string): Observable<IPersonAddress> {
    return this.http.get<IPersonAddress>(
      this.apiUrl.personService + '/Address?id=' + phoneId,
      { withCredentials: true }
    );
  }

  public updatePersonAddress(
    payload: IPersonAddress
  ): Observable<IPersonAddress> {
    return this.http.patch<IPersonAddress>(
      this.apiUrl.personService + '/Address',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonAddress(personId: string): Observable<IPersonAddress> {
    return this.http.delete<IPersonAddress>(
      this.apiUrl.personService + '/Address?id= ' + personId,
      { withCredentials: true }
    );
  }

  public createPersonAddress(
    person: IPersonAddress
  ): Observable<IPersonAddress> {
    return this.http.post<IPersonAddress>(
      this.apiUrl.personService + '/Address',
      person,
      { withCredentials: true }
    );
  }

  public getPersonPhones(personId: string): Observable<IPersonPhone[]> {
    return this.http.get<IPersonPhone[]>(
        this.apiUrl.personService +
        '/Phone/GetByPerson?personId=' + personId,
      { withCredentials: true }
    );
  }

  public getPersonPhone(phoneId: string): Observable<IPersonPhone> {
    return this.http.get<IPersonPhone>(
      this.apiUrl.personService + '/Phone?id=' + phoneId,
      { withCredentials: true }
    );
  }

  public updatePersonPhone(payload: IPersonPhone): Observable<IPersonPhone> {
    return this.http.patch<IPersonPhone>(
      this.apiUrl.personService + '/Phone',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonPhone(personId: string): Observable<IPersonPhone> {
    return this.http.delete<IPersonPhone>(
      this.apiUrl.personService + '/Phone?id= ' + personId,
      { withCredentials: true }
    );
  }

  public createPersonPhone(person: IPersonPhone): Observable<IPersonPhone> {
    return this.http.post<IPersonPhone>(
      this.apiUrl.personService + '/Phone',
      person,
      { withCredentials: true }
    );
  }

  public getPersonAccess(payload: string): Observable<IPersonAccess> {
    return this.http.get<IPersonAccess>(
      this.apiUrl.personService + '/Access?Id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonAccess(payload: IPersonAccess): Observable<IPersonAccess> {
    return this.http.patch<IPersonAccess>(
      this.apiUrl.personService + '/Access',
      payload,
      { withCredentials: true }
    );
  }

  public createPersonAccess(payload: IPersonAccess): Observable<IPersonAccess> {
    return this.http.post<IPersonAccess>(
      this.apiUrl.personService + '/Access',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonAccess(payload: string): Observable<IPersonAccess> {
    return this.http.delete<IPersonAccess>(
      this.apiUrl.personService + '/Access?Id= ' + payload,
      { withCredentials: true }
    );
  }

  public getPersonPreferences(payload: string): Observable<IPersonPreferences> {
    return this.http.get<IPersonPreferences>(
        this.apiUrl.personService +
        '/Preference?id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonPreferences(
    payload: IPersonPreferences
  ): Observable<IPersonPreferences> {
    return this.http.patch<IPersonPreferences>(
      this.apiUrl.personService + '/Preference',
      payload,
      { withCredentials: true }
    );
  }


  public deletePersonPreferences(
    payload: string
  ): Observable<IPersonPreferences> {
    return this.http.delete<IPersonPreferences>(
        this.apiUrl.personService +
        '/Preference?id= ' + payload,
      { withCredentials: true }
    );
  }

  public getPersonResponsible(payload: string): Observable<IPersonResponsible> {
    return this.http.get<IPersonResponsible>(
        this.apiUrl.personService +
        '/Responsible?Id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonResponsible(
    payload: IPersonResponsible
  ): Observable<IPersonResponsible> {
    return this.http.patch<IPersonResponsible>(
      this.apiUrl.personService + '/Responsible',
      payload,
      { withCredentials: true }
    );
  }

  public createPersonResponsible(
    payload: IPersonResponsible
  ): Observable<IPersonResponsible> {
    return this.http.post<IPersonResponsible>(
      this.apiUrl.personService + '/Responsible',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonResponsible(
    payload: string
  ): Observable<IPersonResponsible> {
    return this.http.delete<IPersonResponsible>(
      this.apiUrl.personService +
        '/Responsible?Id= ' + payload,
      { withCredentials: true }
    );
  }

  public getPersonIntegration(payload: string): Observable<IPersonIntegration> {
    return this.http.get<IPersonIntegration>(
      this.apiUrl.personService +
        '/Integration?id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonIntegration(
    payload: IPersonIntegration
  ): Observable<IPersonIntegration> {
    return this.http.patch<IPersonIntegration>(
      this.apiUrl.personService + '/Integration',
      payload,
      { withCredentials: true }
    );
  }

  public createPersonIntegration(
    payload: IPersonIntegration
  ): Observable<IPersonIntegration> {
    return this.http.post<IPersonIntegration>(
      this.apiUrl.personService + '/Integration',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonIntegration(
    payload: string
  ): Observable<IPersonIntegration> {
    return this.http.delete<IPersonIntegration>(
      this.apiUrl.personService +
        '/Integration?Id= ' + payload,
      { withCredentials: true }
    );
  }

  public getPersonGroup(payload: string): Observable<IPersonGroup[]> {
    return this.http.get<IPersonGroup[]>(
      this.apiUrl.personService + '/Group?id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonGroup(payload: IPersonGroup): Observable<IPersonGroup> {
    return this.http.patch<IPersonGroup>(
      this.apiUrl.personService + '/Group',
      payload,
      { withCredentials: true }
    );
  }

  public createPersonGroup(payload: IPersonGroup): Observable<IPersonGroup> {
    return this.http.post<IPersonGroup>(
      this.apiUrl.personService + '/Group',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonGroup(payload: string): Observable<IPersonGroup> {
    return this.http.delete<IPersonGroup>(
      this.apiUrl.personService + '/Group?Id= ' + payload,
      { withCredentials: true }
    );
  }

  public getPersonCertificate(payload: string): Observable<IPersonCertificate> {
    return this.http.get<IPersonCertificate>(
      this.apiUrl.personService +
        '/Certificate?id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonCertificate(
    payload: IPersonCertificate
  ): Observable<IPersonCertificate> {
    return this.http.patch<IPersonCertificate>(
      this.apiUrl.personService + '/Certificate',
      payload,
      { withCredentials: true }
    );
  }

  public createPersonCertificate(
    payload: IPersonCertificate
  ): Observable<IPersonCertificate> {
    return this.http.post<IPersonCertificate>(
      this.apiUrl.personService + '/Certificate',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonCertificate(
    payload: string
  ): Observable<IPersonCertificate> {
    return this.http.delete<IPersonCertificate>(
      this.apiUrl.personService +
        '/Certificate?Id= ' + payload,
      { withCredentials: true }
    );
  }

  public getPersonComment(payload: string): Observable<IPersonComment> {
    return this.http.get<IPersonComment>(
      this.apiUrl.userService + '/Comment?id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonComment(
    payload: IPersonComment
  ): Observable<IPersonComment> {
    return this.http.patch<IPersonComment>(
      this.apiUrl.userService + '/Comment',
      payload,
      { withCredentials: true }
    );
  }

  public createPersonComment(
    payload: IPersonComment
  ): Observable<IPersonComment> {
    return this.http.post<IPersonComment>(
      this.apiUrl.userService + '/Comment',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonComment(payload: string): Observable<IPersonComment> {
    return this.http.delete<IPersonComment>(
      this.apiUrl.userService + '/Comment?Id= ' + payload,
      { withCredentials: true }
    );
  }

  public getPersonTax(payload: string): Observable<IPersonTax> {
    return this.http.get<IPersonTax>(
      this.apiUrl.personService + '/Tax?id=' + payload,
      { withCredentials: true }
    );
  }

  public updatePersonTax(payload: IPersonTax): Observable<IPersonTax> {
    return this.http.patch<IPersonTax>(
      this.apiUrl.personService + '/Tax',
      payload,
      { withCredentials: true }
    );
  }

  public deletePersonTax(payload: string): Observable<IPersonTax> {
    return this.http.delete<IPersonTax>(
      this.apiUrl.personService + '/Tax?Id= ' + payload,
      { withCredentials: true }
    );
  }

  public getSelfPerson(): Observable<IPerson> {
    const authorization: IAuthAccessToken = JSON.parse(
      this.localStorage.getItem('authorization')
    );
    return this.http.get<IPerson>(
      this.apiUrl.personService +
        '/Person?id=' + authorization.PersonId
      ,
      { withCredentials: true }
    );
  }

  public getSupplierById(id: string): Observable<IPerson> {
    return this.http
      .post<any>(`${this.apiUrl.personService}/PersonWithIncludes/GetPersonWithIncludes`, {
      PersonId: id,
      Includes: [
        EPersonWithIncludes.Phones,
        EPersonWithIncludes.Addresses
        ]
      }, {
        withCredentials: true,
        params: { id },
    });
  }

  public createSupplier(supplier: ISupplier): Observable<IPerson> {
    return this.http.post<IPerson>(
      this.apiUrl.personService + '/PersonWithIncludes', supplier,      
      { withCredentials: true }
    );
  }

  public updateSupplier(supplier: ISupplier): Observable<IPerson> {
    return this.http.patch<IPerson>(
      this.apiUrl.personService + '/PersonWithIncludes', supplier,
      { withCredentials: true }
    );
  }

  public getUserDataAsync(): Observable<IPersonDataAsync> {
    return this.http.get<IPersonDataAsync>(
      this.apiUrl.personService + '/Person/GetUserDataAsync',
      { withCredentials: true }
    );
  }
}
